<template>
  <div v-editable="blok" class="rich-text" @click.capture="handleRichTextClick">
    <StoryblokRichTextRenderer :document="blok.content" />
  </div>
</template>

<script setup lang="ts">
import { StoryblokRichText as StoryblokRichTextRenderer } from '@storyblok/vue';
import { useRouter } from 'vue-router';
import { StoryblokRichTextBlok } from '@/features/storyblok/types';

const props = defineProps<{
  blok: StoryblokRichTextBlok;
}>();

const router = useRouter();

const normalizeInternalTarget = (href: string): string | null => {
  const trimmedHref = href.trim();
  if (
    !trimmedHref ||
    trimmedHref.startsWith('#') ||
    trimmedHref.startsWith('mailto:') ||
    trimmedHref.startsWith('tel:')
  ) {
    return null;
  }

  try {
    const url = new URL(trimmedHref, window.location.origin);
    if (url.origin !== window.location.origin) {
      return null;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

const handleRichTextClick = (event: MouseEvent): void => {
  const target = event.target as HTMLElement | null;
  const anchor = target?.closest('a');

  if (!anchor) {
    return;
  }

  const anchorTarget = anchor.getAttribute('target');
  if (anchorTarget === '_blank') {
    return;
  }

  const href = anchor.getAttribute('href');
  if (!href) {
    return;
  }

  const internalTarget = normalizeInternalTarget(href);
  if (!internalTarget) {
    return;
  }

  event.preventDefault();
  void router.push(internalTarget);
};
</script>

<style scoped>
.rich-text {
  width: 100%;
  --sb-rt-heading-color: currentColor;
  --sb-rt-heading-line-height: 1.2;
  --sb-rt-heading-weight: 700;
  --sb-rt-h1-size: clamp(2rem, 2.8vw, 2.8rem);
  --sb-rt-h2-size: clamp(1.7rem, 2.2vw, 2.2rem);
  --sb-rt-h3-size: clamp(1.45rem, 1.8vw, 1.8rem);
  --sb-rt-h4-size: clamp(1.25rem, 1.5vw, 1.5rem);
  --sb-rt-h5-size: clamp(1.1rem, 1.25vw, 1.25rem);
  --sb-rt-heading-letter-spacing: 0;
  --sb-rt-h1-transform: none;
  --sb-rt-heading-margin-top: 1.2em;
  --sb-rt-heading-margin-bottom: 0.5em;
}

.rich-text :deep(h1),
.rich-text :deep(h2),
.rich-text :deep(h3),
.rich-text :deep(h4),
.rich-text :deep(h5) {
  color: var(--sb-rt-heading-color);
  line-height: var(--sb-rt-heading-line-height);
  font-weight: var(--sb-rt-heading-weight);
  letter-spacing: var(--sb-rt-heading-letter-spacing);
}

.rich-text :deep(h1) {
  font-size: var(--sb-rt-h1-size);
}

.rich-text :deep(h2) {
  font-size: var(--sb-rt-h2-size);
}

.rich-text :deep(h3) {
  font-size: var(--sb-rt-h3-size);
}

.rich-text :deep(h4) {
  font-size: var(--sb-rt-h4-size);
}

.rich-text :deep(h5) {
  font-size: var(--sb-rt-h5-size);
}
</style>
